/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { MenuItem } from '@mui/material';
import './biz-select.scss';
import { FormFieldConfig } from '../../../../FrontEndReact/src/Core/Hooks/useForm';
import { getGuid } from '../../../../FrontEndReact/src/Core/Utils/stringUtils';

type BizSelectProps<OptionType> = {
	options: OptionType[];
	onChange: (option: OptionType | null) => void;
	className?: string;
	selectedId: number | string;
	formConfig?: FormFieldConfig;
	disabled?: boolean;
	noSelectText?: string;
	name?: string;
};

export function BizSelect<OptionType extends { Id: number | string; Name: string }>({
	options,
	onChange,
	className,
	selectedId,
	formConfig,
	disabled,
	noSelectText,
	name: pName,
}: BizSelectProps<OptionType>) {
	const [name] = useState(pName ?? getGuid());
	const isValid = formConfig?.state.fields[name]?.isValid ?? true;

	useEffect(() => {
		if (!formConfig || !formConfig.validators) return;

		const value = selectedId && selectedId != '-1' ? selectedId.toString() : '';
		const newIsValid = formConfig.state.validateFieldValue(value, formConfig.validators);
		if (newIsValid !== isValid) {
			formConfig.state.setFieldValid(name, newIsValid, formConfig.group);
		}
	}, [selectedId, formConfig?.validators]);

	function onChangeHandler(event: SelectChangeEvent) {
		const selectedOptionId = event.target.value;
		onChange(options.find((o) => o.Id === (typeof o.Id === 'number' ? +selectedOptionId : selectedOptionId)) ?? null);

		if (formConfig) {
			formConfig.state.setDirty();
		}
	}

	let cssClasses = `biz-select ${className}`;

	if (!isValid) {
		cssClasses += ' invalid';
	}

	const getSelectedValue = () => {
		const option = options.find((o) => o.Id === selectedId);
		return option ? String(option.Id) : '-1';
	};

	const selectedValue = getSelectedValue();

	return (
		<Select className={cssClasses} onChange={onChangeHandler} value={selectedValue} disabled={disabled}>
			{noSelectText && <MenuItem value='-1'>{noSelectText}</MenuItem>}
			{options.map((o) => (
				<MenuItem value={o.Id} key={o.Id}>
					{o.Name}
				</MenuItem>
			))}
		</Select>
	);
}
